import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const sharedProps: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	portal: 'SP',
	feature: HubFeature.OtherRequirement,
};

const pathStart = 'Catalogs|Other Requirements|Advanced|Details|';

export type SPOtherRequirementsDetailsLocNames =
	| 'SPOtherRequirementsDetailsPageTitle'
	| 'SPOtherRequirementsDetailsLabelName'
	| 'SPOtherRequirementsDetailsLabelDisplayOrder'
	| 'SPOtherRequirementsDetailsLabelProgram'
	| 'SPOtherRequirementsDetailsButtonEdit'
	| 'SPOtherRequirementsDetailsButtonCancel'
	| 'SPOtherRequirementsDetailsButtonUpdate'
	| 'SPOtherRequirementsDetailsButtonCreate'
	| 'SPOtherRequirementsDetailsUpdateSuccess'
	| 'SPOtherRequirementsDetailsUpdateFailed'
	| 'SPOtherRequirementsDetailsUpdateError'
	| 'SPOtherRequirementsDetailsLabelGroup'
	| 'SPOtherRequirementsDetailsLabelStartDate'
	| 'SPOtherRequirementsDetailsLabelEndDate'
	| 'SPOtherRequirementsDetailsLabelPoints'
	| 'SPOtherRequirementsDetailsLabelMaxPoints'
	| 'SPOtherRequirementsDetailsLabelFactor'
	| 'SPOtherRequirementsDetailsLabelCreateAge'
	| 'SPOtherRequirementsDetailsLabelValidPeriod';

export const spOtherRequirementsDetailsLocs: LocDefs<SPOtherRequirementsDetailsLocNames> = {
	SPOtherRequirementsDetailsPageTitle: {
		defaultValue: 'Subdivision Details',
		description: 'The title of the subdivision details page.',
		displayPath: `${pathStart}Title Page`,
		displayType: 'Title',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelName: {
		defaultValue: 'Name',
		description: 'The label for the subdivision name.',
		displayPath: `${pathStart}Label Name`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelDisplayOrder: {
		defaultValue: 'Display Order',
		description: 'The label for the subdivision display order.',
		displayPath: `${pathStart}Label Display Order`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelProgram: {
		defaultValue: 'Program',
		description: 'The label for the subdivision program.',
		displayPath: `${pathStart}Label Program`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsButtonEdit: {
		defaultValue: 'Edit',
		description: 'The button text for editing a subdivision.',
		displayPath: `${pathStart}Button Edit`,
		displayType: 'Button',
		...sharedProps,
	},

	SPOtherRequirementsDetailsButtonCancel: {
		defaultValue: 'Cancel',
		description: 'The button text for canceling edits.',
		displayPath: `${pathStart}Button Cancel`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsDetailsButtonUpdate: {
		defaultValue: 'Update',
		description: 'The button text for updating a subdivision.',
		displayPath: `${pathStart}Button Update`,
		displayType: 'Button',
		...sharedProps,
	},

	SPOtherRequirementsDetailsButtonCreate: {
		defaultValue: 'Create',
		description: 'The button text for creating a subdivision.',
		displayPath: `${pathStart}Button Create`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelGroup: {
		defaultValue: 'Group',
		description: 'The label for the subdivision group.',
		displayPath: `${pathStart}Label Group`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelStartDate: {
		defaultValue: 'Start Date',
		description: 'The label for the subdivision start date.',
		displayPath: `${pathStart}Label Start Date`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelEndDate: {
		defaultValue: 'End Date',
		description: 'The label for the subdivision end date.',
		displayPath: `${pathStart}Label End Date`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelPoints: {
		defaultValue: 'Points',
		description: 'The label for the subdivision points.',
		displayPath: `${pathStart}Label Points`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelMaxPoints: {
		defaultValue: 'Max Points',
		description: 'The label for the subdivision max points.',
		displayPath: `${pathStart}Label Max Points`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelFactor: {
		defaultValue: 'Factor',
		description: 'The label for the subdivision factor.',
		displayPath: `${pathStart}Label Factor`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelCreateAge: {
		defaultValue: 'Create Age',
		description: 'The label for the subdivision create age.',
		displayPath: `${pathStart}Label Create Age`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsLabelValidPeriod: {
		defaultValue: 'Valid Period',
		description: 'The label for the subdivision valid period.',
		displayPath: `${pathStart}Label Valid Period`,
		displayType: 'Label',
		...sharedProps,
	},

	SPOtherRequirementsDetailsUpdateSuccess: {
		defaultValue: 'Subdivision updated successfully',
		description: 'The success message when a subdivision is updated.',
		displayPath: `${pathStart}Update Success`,
		displayType: 'Notification',
		...sharedProps,
	},
	SPOtherRequirementsDetailsUpdateFailed: {
		defaultValue: 'Failed to update subdivision',
		description: 'The error message when a subdivision update fails.',
		displayPath: `${pathStart}Update Failed`,
		displayType: 'Notification',
		...sharedProps,
	},
	SPOtherRequirementsDetailsUpdateError: {
		defaultValue: 'Error updating subdivision',
		description: 'The error message when a subdivision update encounters an error.',
		displayPath: `${pathStart}Update Error`,
		displayType: 'Notification',
		...sharedProps,
	},
};

export const LOC_KEYS = MapLocKeys<SPOtherRequirementsDetailsLocNames>(
	spOtherRequirementsDetailsLocs
);

export type SPOtherRequirementsDetailsLocType = LocMap<SPOtherRequirementsDetailsLocNames>;
